"use client";

import React, { useEffect, useState } from "react";
import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Plus, CreditCard } from "lucide-react";
import { useAppServices } from "hook/services";
import Select from "react-select"
import { useUserInfo } from "context/user";
interface propTypes {
  handleRefresh: () => void;
  walletId: string;
  paymentMethods: any[];
}

const getUserIP = async (): Promise<string | null> => {
  try {
    const services = [
      'https://api.ipify.org?format=json',
      'https://ipapi.co/json/',
      'https://jsonip.com/'
    ];

    for (const service of services) {
      try {
        const response = await fetch(service, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (service.includes('ipify')) {
            return data.ip;
          } else if (service.includes('ipapi')) {
            return data.ip;
          } else if (service.includes('jsonip')) {
            return data.ip;
          }
        }
      } catch (error) {
        console.warn(`IP service failed:`, error);
        continue;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Failed to get user IP:', error);
    return null;
  }
};

const AddBalance = ({ handleRefresh, walletId, paymentMethods }: propTypes) => {
  const AppService = useAppServices();
  const {user}=useUserInfo()
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
 const [userIP, setUserIP] = useState<string | null>(null);
  const [ipLoading, setIpLoading] = useState<boolean>(false);
  
  const paymentMethodOptions = paymentMethods?.map((method) => ({
    value: method._id,
    label: `${method.brand} •••• ${method.last4}${
      method.isDefault ? " (Default)" : ""
    }`,
  }));
  const toggle = () => {
    setShow((prev) => !prev);
    setErrorMsg("");
    setLoading(false);
    formik.resetForm();
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setErrorMsg("");

    try {
      const payload = {
        _id: walletId, // wallet ID
        userId: user?._id, // wallet ID
        amount: parseFloat(values.amount),
        ipAddress:userIP,
        paymentMethodId: values.paymentMethodId // selected payment method ID
      };

      const { response } = await AppService.wallet.add_balance({
        payload,
        toaster: true,
      });

      if (response) {
        toggle();
        handleRefresh();
      }
    } catch (error) {
      setErrorMsg("Failed to add funds");
    } finally {
      setLoading(false);
    }
  };
 const handleChange = (selectedOption:any) => {
    formik.setFieldValue("paymentMethodId", selectedOption?.value || "");
  };
  // Formik setup
  const formik = useFormik({
    initialValues: {
      amount: "",
      paymentMethodId: "",
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .required("Amount is required")
        .min(1, "Minimum amount is $1")
        .max(10000, "Maximum amount is $10,000"),
      paymentMethodId: Yup.string()
        .required("Please select a payment method")
    }),
    onSubmit: (values) => handleSubmit(values),
  });
 useEffect(() => {
    const fetchIP = async () => {
      setIpLoading(true);
      try {
        const ip = await getUserIP();
        setUserIP(ip);
      } catch (error) {
        console.error("Failed to fetch user IP:", error);
        setUserIP(null);
      } finally {
        setIpLoading(false);
      }
    };

    fetchIP();
  }, []);
  return (
    <>
      {/* Add Fund Button */}
      <button
        className="flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 hover:scale-105 hover:shadow-lg border border-white/20"
        onClick={toggle}
      >
        <Plus size={20} className="mr-2" />
        Add Funds
      </button>

      {/* Modal */}
      <Modal
        show={show}
        onHide={toggle}
        id="fundModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white dark:bg-zink-700 shadow rounded-md dark:shadow-zink-900/50"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500/20">
          <Modal.Title className="text-16 font-semibold text-zink-800 dark:text-zink-100">
            Add Funds to Wallet
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Amount */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Amount (USD)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">
                  $
                </span>
                <input
                  type="number"
                  name="amount"
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.amount}
                  step="0.01"
                  min="1"
                  max="10000"
                />
              </div>
              {formik.touched.amount && formik.errors.amount && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                  {formik.errors.amount}
                </p>
              )}
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Payment Method
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Select
                         options={paymentMethodOptions}
                         value={paymentMethodOptions?.find(
                           (opt) => opt.value === formik.values.paymentMethodId
                         )}
                         onChange={handleChange}
                         onBlur={formik.handleBlur}
                         name="paymentMethodId"
                         placeholder="Select a payment method"
                         isSearchable
                         noOptionsMessage={() =>
                           paymentMethods?.length === 0
                             ? "No payment methods available. Please add one."
                             : "No matching payment methods"
                         }
                       />
              </div>
              {formik.touched.paymentMethodId && formik.errors.paymentMethodId && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                  {formik.errors.paymentMethodId}
                </p>
              )}
              
              {paymentMethods?.length === 0 && (
                <p className="text-amber-500 dark:text-amber-400 text-sm mt-1">
                  No payment methods available. Please add a payment method first.
                </p>
              )}
            </div>

            {/* Error Message */}
            {errorMsg && (
              <div className="text-red-500 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                {errorMsg}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 dark:text-red-400 bg-white dark:bg-zink-600 border border-slate-200 dark:border-zink-500 px-4 py-2 rounded-md hover:text-red-600 dark:hover:text-red-300 hover:bg-slate-50 dark:hover:bg-zink-500 transition-colors"
                onClick={toggle}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || paymentMethods?.length === 0}
                className="text-white bg-custom-500 border-custom-500 hover:bg-custom-600 dark:bg-custom-500 dark:border-custom-500 dark:hover:bg-custom-600 px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Processing..." : "Add Funds"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddBalance;